import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';

@Directive({
  selector: '[appInfiniteScroll]',
  standalone: false
})
export class InfiniteScrollDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() root: Element | null = null;
  @Input() rootMargin = '200px';
  @Input() threshold = 0;
  @Input() disabled = false;

  @Output() reached = new EventEmitter<void>();

  private observer?: IntersectionObserver;
  private scrollTarget?: HTMLElement;
  private scrollRafId = 0;

  constructor(private element: ElementRef<HTMLElement>) {}

  ngAfterViewInit() {
    this.initObserver();
    this.attachScrollListener();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled'] && !this.disabled) {
      requestAnimationFrame(() => this.refreshObservation());
    }

    if (changes['root'] && !changes['root'].firstChange) {
      this.detachScrollListener();
      this.observer?.disconnect();
      this.initObserver();
      this.attachScrollListener();
    }
  }

  ngOnDestroy() {
    if (this.scrollRafId) {
      cancelAnimationFrame(this.scrollRafId);
    }
    this.detachScrollListener();
    this.observer?.disconnect();
  }

  private initObserver() {
    this.observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];

        if (entry.isIntersecting && !this.disabled) {
          this.reached.emit();
        }
      },
      {
        root: this.root,
        rootMargin: this.rootMargin,
        threshold: this.threshold
      }
    );

    this.observer.observe(this.element.nativeElement);
  }

  private refreshObservation() {
    if (!this.observer || this.disabled) {
      return;
    }

    const target = this.element.nativeElement;
    this.observer.unobserve(target);
    this.observer.observe(target);
  }

  private attachScrollListener() {
    if (this.root instanceof HTMLElement) {
      this.scrollTarget = this.root;
      this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  private detachScrollListener() {
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollTarget = undefined;
  }

  private onScroll = () => {
    if (this.disabled || this.scrollRafId) {
      return;
    }

    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = 0;
      this.refreshObservation();
    });
  };
}
